import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Drawer,
    Typography,
} from "@mui/material";
import React, { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../AppContext";
import { PosContext } from "../../shared/PosContext";
import { getTakeawayUrlSegment } from "../../utils/floorUtils";
import { useCallCenterDraftOrders } from "../../shared/hooks/useCallCenterOrdersData";
import { usePrintOrder } from "../../shared/hooks/useTablesData";
import { dateTimeFormat } from "../../utils/dateUtils";
import { getTableOrderPrintFormat } from "../../utils/printFormatUrl";

const CallCenterOrdersDrawer = ({ open, onClose }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const printOrder = usePrintOrder();
    const { posProfile } = useContext(AppContext);
    const { takeawayTable } = useContext(PosContext);
    const tableOrderPrintFormat = getTableOrderPrintFormat(posProfile);

    const takeawayPathSegment = useMemo(
        () => getTakeawayUrlSegment(posProfile, takeawayTable),
        [posProfile, takeawayTable]
    );

    const {
        data: orders,
        isLoading,
        refetch,
        callCenterUsersLoading,
    } = useCallCenterDraftOrders();

    const list = useMemo(() => {
        return Array.isArray(orders) ? orders : [];
    }, [orders]);

    const handleOpenOrder = (orderName) => {
        if (!orderName) return;
        onClose?.();
        navigate(`/pos/${takeawayPathSegment}/${orderName}`);
    };

    const handlePrintOrder = (orderName) => {
        if (!orderName) return;
        printOrder.mutate({
            orderId: orderName,
            doctype: "Table Order",
            printFormat: tableOrderPrintFormat,
        });
    };

    return (
        <Drawer anchor="right" open={!!open} onClose={onClose}>
            <Box sx={{ width: 420, p: 2 }}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1}
                >
                    <Typography variant="h6">
                        {t("callCenterOrders.title")}
                    </Typography>
                    <Button variant="outlined" onClick={() => refetch?.()}>
                        {t("common.refresh")}
                    </Button>
                </Box>

                <Typography variant="body2" color="text.secondary" mb={2}>
                    {t("callCenterOrders.subtitle")}
                </Typography>

                {(isLoading || callCenterUsersLoading) && (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        py={3}
                        gap={1}
                    >
                        <CircularProgress size={20} />
                        <Typography variant="body2">
                            {t("common.loading")}
                        </Typography>
                    </Box>
                )}

                {!isLoading && !callCenterUsersLoading && list.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        {t("callCenterOrders.empty")}
                    </Typography>
                )}

                {!isLoading &&
                    !callCenterUsersLoading &&
                    list.map((o) => {
                        const orderName = o?.name;
                        const customer = o?.customer || "-";
                        const amount = Number(o?.amount || 0);
                        const provider = String(o?.delivery_provider || "").trim();
                        const designated = String(o?.designated_number || "").trim();
                        const kind = provider
                            ? t("callCenterOrders.kindDelivery")
                            : designated
                              ? t("callCenterOrders.kindInternal")
                              : t("callCenterOrders.kindExternal");
                        return (
                            <Box key={orderName} sx={{ mb: 1 }}>
                                <Box
                                    display="flex"
                                    alignItems="flex-start"
                                    justifyContent="space-between"
                                    gap={1}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="subtitle2"
                                            noWrap
                                            title={orderName}
                                        >
                                            {orderName}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            noWrap
                                        >
                                            {customer} • {kind} • ₪
                                            {amount.toFixed(2)}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                        >
                                            {t("callCenterOrders.owner")}:{" "}
                                            {o?.owner || "-"} •{" "}
                                            {dateTimeFormat(o?.modified)}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box display="flex" gap={1} mt={1}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleOpenOrder(orderName)}
                                    >
                                        {t("callCenterOrders.open")}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handlePrintOrder(orderName)}
                                        disabled={printOrder.isLoading}
                                    >
                                        {t("callCenterOrders.printOrder")}
                                    </Button>
                                </Box>

                                <Divider sx={{ mt: 1.5 }} />
                            </Box>
                        );
                    })}
            </Box>
        </Drawer>
    );
};

export default CallCenterOrdersDrawer;

